import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { API, toast } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Search, ExternalLink, Download, Upload, ChevronLeft, ChevronRight, Loader2, Globe, AlertCircle, Image, Save } from 'lucide-react';

const SOURCE_STYLES = {
  wikimedia:    { label: 'Wikimedia',    bg: 'bg-orange-500/15', text: 'text-orange-400', border: 'border-orange-500/30' },
  pixabay:      { label: 'Pixabay',      bg: 'bg-green-500/15',  text: 'text-green-400',  border: 'border-green-500/30' },
  pexels:       { label: 'Pexels',       bg: 'bg-teal-500/15',   text: 'text-teal-400',   border: 'border-teal-500/30' },
  flickr:       { label: 'Flickr',       bg: 'bg-pink-500/15',   text: 'text-pink-400',   border: 'border-pink-500/30' },
  shutterstock: { label: 'Shutterstock', bg: 'bg-red-500/15',    text: 'text-red-400',    border: 'border-red-500/30' },
};

export default function Periscope() {
  const [query, setQuery] = useState('');
  const [images, setImages] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [sources, setSources] = useState({});
  const [errors, setErrors] = useState({});
  const [wpSites, setWpSites] = useState([]);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [uploadedMap, setUploadedMap] = useState({});
  const [uploadMeta, setUploadMeta] = useState({ filename: '', title: '', alt_text: '' });
  const [downloadModal, setDownloadModal] = useState(null); // Image à télécharger
  const [downloadUrl, setDownloadUrl] = useState(null); // URL blob pour téléchargement
  const [downloadLoading, setDownloadLoading] = useState(false);

  const doSearch = useCallback(async (q, p) => {
    if (!q.trim()) return;
    setLoading(true);
    try {
      const [searchRes, sitesRes] = await Promise.all([
        axios.get(`${API}/periscope/search`, { params: { q: q.trim(), page: p } }),
        wpSites.length ? Promise.resolve({ data: wpSites }) : axios.get(`${API}/wordpress-sites`),
      ]);
      setImages(searchRes.data.images || []);
      setSources(searchRes.data.sources || {});
      setErrors(searchRes.data.errors || {});
      if (!wpSites.length) setWpSites(sitesRes.data || []);
      setSearched(true);
    } catch (e) { toast.error('Erreur de recherche'); }
    finally { setLoading(false); }
  }, [wpSites]);

  const handleSearch = (e) => { e?.preventDefault(); setPage(1); doSearch(query, 1); };
  const goPage = (p) => { setPage(p); doSearch(query, p); window.scrollTo(0, 0); };

  const handleDownload = async (img) => {
    if (!img.full_url) {
      toast.error('Cette image n\'est pas téléchargeable');
      return;
    }
    
    // Ouvrir la modale avec l'image
    setDownloadModal(img);
    setDownloadUrl(null);
    setDownloadLoading(true);
    
    try {
      const url = `${API}/periscope/download?url=${encodeURIComponent(img.full_url)}&filename=${encodeURIComponent(img.title?.replace(/[^a-zA-Z0-9_-]/g, '_') || 'image')}`;
      
      const response = await fetch(url);
      if (!response.ok) throw new Error('Erreur téléchargement');
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      setDownloadUrl(blobUrl);
      
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Erreur lors du chargement de l\'image');
      setDownloadModal(null);
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleSaveImage = () => {
    if (!downloadUrl || !downloadModal) return;
    
    const filename = (downloadModal.title?.replace(/[^a-zA-Z0-9_-]/g, '_') || 'image').substring(0, 60) + '.jpg';
    
    // Méthode 1: Créer un lien avec download
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    
    // Méthode 2: Fallback avec setTimeout
    setTimeout(() => {
      document.body.removeChild(link);
    }, 100);
    
    toast.success(`Téléchargement de "${filename}" lancé`);
  };

  const closeDownloadModal = () => {
    if (downloadUrl) {
      window.URL.revokeObjectURL(downloadUrl);
    }
    setDownloadModal(null);
    setDownloadUrl(null);
  };

  const handleUploadToWp = async (img, siteId) => {
    const key = `${img.id}_${siteId}`;
    setUploading(key);
    try {
      const res = await axios.post(`${API}/periscope/upload-to-wp`, {
        image_url: img.full_url, site_id: siteId,
        filename: (uploadMeta.filename || img.title?.replace(/[^a-zA-Z0-9_-]/g, '_') || 'image') + '.jpg',
        title: uploadMeta.title || img.title || '',
        alt_text: uploadMeta.alt_text || img.title || '',
      });
      if (res.data.success) {
        toast.success(`Image televersee sur ${wpSites.find(s => s.id === siteId)?.name}`);
        setUploadedMap(prev => ({ ...prev, [key]: res.data.media_url }));
      } else { toast.error(res.data.error || 'Erreur de televersement'); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur de televersement'); }
    finally { setUploading(null); }
  };

  return (
    <div className="min-h-screen p-6" data-testid="periscope-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold tracking-wider uppercase"
          style={{ fontFamily: "'Orbitron', monospace", color: '#00e5ff', textShadow: '0 0 20px rgba(0,229,255,0.3)' }}>
          PERISCOPE
        </h1>
        <p className="text-slate-500 font-mono text-sm">Recherche d'images multi-sources</p>
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-3 mb-6">
        <Input value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Rechercher des images..."
          className="flex-1 bg-slate-900/50 border-slate-700 text-slate-200 text-base h-11"
          data-testid="periscope-search-input" />
        <Button type="submit" disabled={loading || !query.trim()} className="bg-sky-600 hover:bg-sky-500 h-11 px-6" data-testid="periscope-search-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          <span className="ml-2 hidden sm:inline">Rechercher</span>
        </Button>
      </form>

      {/* Source status */}
      {searched && (
        <div className="flex flex-wrap items-center gap-2 mb-5">
          {Object.entries(SOURCE_STYLES).map(([key, style]) => {
            const active = sources[key];
            const hasError = errors[key];
            return (
              <span key={key} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded font-mono text-[11px] uppercase tracking-wider border ${active ? `${style.bg} ${style.text} ${style.border}` : 'bg-slate-900/30 text-slate-600 border-slate-800'}`}
                data-testid={`source-badge-${key}`}>
                {hasError && <AlertCircle className="w-3 h-3" />}
                {style.label}
                {!active && ' (non config.)'}
              </span>
            );
          })}
        </div>
      )}

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-sky-400" />
          <span className="ml-3 text-slate-400 font-mono">Recherche en cours...</span>
        </div>
      ) : !searched ? (
        <Card className="bg-slate-900/30 border-slate-800">
          <CardContent className="py-16 text-center">
            <Image className="w-12 h-12 text-slate-700 mx-auto mb-4" />
            <p className="text-slate-500 font-mono text-sm">Entrez un mot-cle pour rechercher des images</p>
          </CardContent>
        </Card>
      ) : images.length === 0 ? (
        <Card className="bg-slate-900/30 border-slate-800">
          <CardContent className="py-12 text-center">
            <p className="text-slate-500 font-mono text-sm">Aucun resultat pour "{query}"</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Periscope grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 mb-6">
            {images.map(img => {
              const style = SOURCE_STYLES[img.source] || SOURCE_STYLES.wikimedia;
              const isShutterstock = img.source === 'shutterstock';
              return (
                <div key={img.id} className="flex flex-col items-center group" data-testid={`image-card-${img.id}`}>
                  {/* Periscope viewport */}
                  <div className="relative w-full aspect-square rounded-full overflow-hidden border-[3px] border-slate-600/80 shadow-[0_0_15px_rgba(0,229,255,0.15),inset_0_0_30px_rgba(0,0,0,0.6)]"
                    style={{ background: 'radial-gradient(circle, transparent 55%, rgba(0,10,20,0.85) 100%)' }}>
                    <img src={img.preview_url} alt={img.title} loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      onError={(e) => { e.target.style.display = 'none'; }} />
                    {/* Vignette overlay */}
                    <div className="absolute inset-0 rounded-full pointer-events-none"
                      style={{ boxShadow: 'inset 0 0 50px 20px rgba(0,5,15,0.7), inset 0 0 100px 40px rgba(0,5,15,0.3)' }} />
                    {/* Crosshair */}
                    <div className="absolute inset-0 pointer-events-none flex items-center justify-center opacity-20 group-hover:opacity-40 transition-opacity">
                      <div className="absolute w-[1px] h-full bg-sky-400/50" />
                      <div className="absolute w-full h-[1px] bg-sky-400/50" />
                      <div className="w-8 h-8 rounded-full border border-sky-400/50" />
                    </div>
                    {/* Source badge */}
                    <span className={`absolute bottom-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-wider border backdrop-blur-sm ${style.bg} ${style.text} ${style.border}`}>
                      {style.label}
                    </span>
                    {isShutterstock && (
                      <span className="absolute top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-[8px] font-mono bg-slate-900/80 text-slate-400 border border-slate-700 backdrop-blur-sm">
                        Preview
                      </span>
                    )}
                  </div>
                  {/* Info */}
                  <div className="mt-2 w-full text-center px-1">
                    <p className="text-slate-300 text-xs font-medium line-clamp-1">{img.title || 'Sans titre'}</p>
                    {img.author && <p className="text-slate-600 text-[10px] font-mono">{img.author}</p>}
                  </div>
                  {/* Actions */}
                  <div className="flex gap-1.5 mt-1.5">
                    <a href={img.source_page_url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-1 px-2.5 h-7 rounded-full border border-slate-700 text-slate-400 hover:text-sky-400 hover:border-sky-500/50 transition-colors text-[10px] font-mono"
                      data-testid={`source-link-${img.id}`}>
                      <ExternalLink className="w-3 h-3" />{isShutterstock ? 'Acheter' : 'Source'}
                    </a>
                    {img.downloadable && (
                      <button onClick={() => handleDownload(img)}
                        className="flex items-center gap-1 px-2.5 h-7 rounded-full border border-slate-700 text-slate-400 hover:text-green-400 hover:border-green-500/50 transition-colors text-[10px] font-mono"
                        data-testid={`download-btn-${img.id}`}>
                        <Download className="w-3 h-3" />
                        DL
                      </button>
                    )}
                    {img.downloadable && (
                      <button onClick={() => {
                        const defaultName = img.title?.replace(/[^a-zA-Z0-9_-]/g, '_')?.substring(0, 60) || 'image';
                        setUploadMeta({ filename: defaultName, title: img.title || '', alt_text: img.title || '' });
                        setUploadTarget(img);
                      }}
                        disabled={!!uploading}
                        className="flex items-center gap-1 px-2.5 h-7 rounded-full border border-slate-700 text-slate-400 hover:text-amber-400 hover:border-amber-500/50 transition-colors text-[10px] font-mono disabled:opacity-50"
                        data-testid={`upload-btn-${img.id}`}>
                        {uploading?.startsWith(img.id) ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                        WP
                      </button>
                    )}
                  </div>
                  {/* Uploaded links */}
                  {Object.entries(uploadedMap).filter(([k]) => k.startsWith(img.id + '_')).map(([k, url]) => {
                    const siteId = k.split('_').pop();
                    const siteName = wpSites.find(s => s.id === siteId)?.name || siteId;
                    return (
                      <div key={k} className="mt-1 flex items-center gap-1 text-[9px] text-emerald-400 font-mono">
                        <Globe className="w-2.5 h-2.5" />{siteName}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-center gap-3 py-4">
            <Button variant="outline" size="sm" disabled={page <= 1}
              onClick={() => goPage(page - 1)} className="border-slate-700 text-slate-400" data-testid="prev-page">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            {[1, 2, 3].map(p => (
              <Button key={p} variant={p === page ? 'default' : 'outline'} size="sm"
                onClick={() => goPage(p)}
                className={p === page ? 'bg-sky-600 text-white' : 'border-slate-700 text-slate-400'}
                data-testid={`page-${p}`}>
                {p}
              </Button>
            ))}
            <Button variant="outline" size="sm" disabled={page >= 3}
              onClick={() => goPage(page + 1)} className="border-slate-700 text-slate-400" data-testid="next-page">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </>
      )}

      {/* Upload site selector dialog */}
      <Dialog open={!!uploadTarget} onOpenChange={(open) => { if (!open) setUploadTarget(null); }}>
        <DialogContent className="bg-slate-950 border-sky-500/50 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-sky-400" style={{ fontFamily: "'Orbitron', monospace" }}>Televerser vers WordPress</DialogTitle>
          </DialogHeader>
          {uploadTarget && (
            <div className="space-y-4 mt-2">
              <div className="flex items-center gap-3 p-2 bg-slate-900/50 rounded border border-slate-800">
                <img src={uploadTarget.preview_url} alt="" className="w-14 h-14 object-cover rounded" />
                <div className="min-w-0 flex-1">
                  <p className="text-slate-300 text-sm font-medium truncate max-w-[250px]">{(uploadTarget.title || 'Image').split(',').slice(0, 3).join(', ')}</p>
                  <p className="text-slate-600 text-xs font-mono">{SOURCE_STYLES[uploadTarget.source]?.label}</p>
                </div>
              </div>

              {/* Métadonnées éditables */}
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider">Nom du fichier</label>
                  <Input value={uploadMeta.filename}
                    onChange={(e) => setUploadMeta(prev => ({ ...prev, filename: e.target.value }))}
                    className="mt-1 bg-slate-950/50 border-slate-700 text-slate-200 text-sm"
                    placeholder="nom-du-fichier"
                    data-testid="upload-meta-filename" />
                  <p className="text-[10px] text-slate-600 mt-0.5">.jpg sera ajoute automatiquement</p>
                </div>
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider">Titre de la photo</label>
                  <Input value={uploadMeta.title}
                    onChange={(e) => setUploadMeta(prev => ({ ...prev, title: e.target.value }))}
                    className="mt-1 bg-slate-950/50 border-slate-700 text-slate-200 text-sm"
                    placeholder="Titre descriptif de l'image"
                    data-testid="upload-meta-title" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider">Texte alternatif (alt)</label>
                  <Input value={uploadMeta.alt_text}
                    onChange={(e) => setUploadMeta(prev => ({ ...prev, alt_text: e.target.value }))}
                    className="mt-1 bg-slate-950/50 border-slate-700 text-slate-200 text-sm"
                    placeholder="Description pour l'accessibilite et le SEO"
                    data-testid="upload-meta-alt" />
                </div>
              </div>

              <p className="text-slate-400 text-sm">Choisissez le site cible :</p>
              <div className="space-y-2">
                {wpSites.map(site => {
                  const key = `${uploadTarget.id}_${site.id}`;
                  const done = uploadedMap[key];
                  return (
                    <button key={site.id}
                      onClick={() => handleUploadToWp(uploadTarget, site.id)}
                      disabled={!!uploading || done}
                      className={`w-full flex items-center gap-3 p-3 rounded border transition-all text-left ${done ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-slate-900/50 border-slate-700 hover:border-sky-500/50 hover:bg-sky-500/5'}`}
                      data-testid={`upload-site-${site.id}`}>
                      <Globe className={`w-4 h-4 flex-shrink-0 ${done ? 'text-emerald-400' : 'text-slate-500'}`} />
                      <span className={`text-sm font-mono flex-1 ${done ? 'text-emerald-400' : 'text-slate-300'}`}>{site.name}</span>
                      {uploading === key && <Loader2 className="w-4 h-4 animate-spin text-sky-400" />}
                      {done && <span className="text-[10px] text-emerald-400 font-mono">OK</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Téléchargement */}
      <Dialog open={!!downloadModal} onOpenChange={(open) => !open && closeDownloadModal()}>
        <DialogContent className="bg-slate-950 border-slate-800 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-sky-400 font-orbitron tracking-wider">
              TÉLÉCHARGER L'IMAGE
            </DialogTitle>
          </DialogHeader>
          
          {downloadModal && (
            <div className="space-y-4">
              {/* Aperçu de l'image */}
              <div className="relative bg-slate-900/50 rounded-lg overflow-hidden flex items-center justify-center" style={{ maxHeight: '50vh' }}>
                {downloadLoading ? (
                  <div className="flex flex-col items-center gap-3 py-20">
                    <Loader2 className="w-8 h-8 animate-spin text-sky-400" />
                    <span className="text-slate-400 text-sm font-mono">Chargement...</span>
                  </div>
                ) : downloadUrl ? (
                  <img 
                    src={downloadUrl} 
                    alt={downloadModal.title} 
                    className="max-w-full max-h-[50vh] object-contain"
                  />
                ) : null}
              </div>

              {/* Infos */}
              <div className="text-sm text-slate-400 font-mono">
                <p className="line-clamp-2">{downloadModal.title}</p>
                <p className="text-xs text-slate-500">Source: {downloadModal.source}</p>
              </div>

              {/* Boutons */}
              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={closeDownloadModal}
                  className="border-slate-700 text-slate-400 hover:text-white"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleSaveImage}
                  disabled={!downloadUrl || downloadLoading}
                  className="bg-green-600 hover:bg-green-500 text-white"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer sous...
                </Button>
              </div>

              {/* Instructions */}
              <p className="text-xs text-slate-500 text-center">
                Si le téléchargement ne démarre pas, faites clic droit sur l'image → "Enregistrer l'image sous..."
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
